import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Globe, Calendar, DollarSign, FileText, ClipboardCheck, ExternalLink, Loader2, AlertTriangle, Bell, CheckCircle2, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { useAuth } from '../context/AuthContext';
import { STATE_NAMES } from '../data/usStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEST_POLICY_LABELS = {
  A: 'Tests always required',
  F: 'Test flexible',
  I: 'Tests ignored',
  N: 'Tests never required',
  S: 'Tests sometimes required',
};

const PLAN_LABELS = [
  { key: 'ed', label: 'Early Decision', short: 'ED' },
  { key: 'edii', label: 'Early Decision II', short: 'EDII' },
  { key: 'ea', label: 'Early Action', short: 'EA' },
  { key: 'eaii', label: 'Early Action II', short: 'EAII' },
  { key: 'rea', label: 'Restrictive EA', short: 'REA' },
  { key: 'rd_rolling', label: 'Regular Decision / Rolling', short: 'RD' },
];

function DeadlineTile({ short, label, value, testid }) {
  const has = !!value && value !== 'None';
  return (
    <div
      className={`rounded-xl border p-4 ${has ? 'bg-white border-[#0F392B]/20' : 'bg-[#F9F9F7] border-[#E2E2E2]'}`}
      data-testid={testid}
    >
      <p className="text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1">{short}</p>
      <p className="text-[11px] text-muted-foreground mb-2 leading-tight">{label}</p>
      <p className={`text-base font-semibold ${has ? 'text-[#0F392B]' : 'text-[#C9C9C0]'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
        {has ? value : '—'}
      </p>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  const has = value && value !== 'None';
  return (
    <div className="flex items-start gap-3 py-3 border-b border-[#E2E2E2] last:border-b-0">
      <Icon className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
      <div className="flex-1">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium mb-0.5">{label}</p>
        <p className={`text-sm ${has ? 'text-foreground font-medium' : 'text-muted-foreground italic'}`}>
          {has ? value : 'Not required / see college website'}
        </p>
      </div>
    </div>
  );
}

function RelatedCard({ r }) {
  return (
    <Link
      to={`/deadlines-grid/${r.slug}`}
      className="block rounded-xl border border-[#E2E2E2] bg-white p-4 hover:shadow-md hover:-translate-y-0.5 transition-all"
      data-testid={`related-card-${r.slug}`}
    >
      <p className="text-sm font-semibold text-[#0F392B] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{r.name}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{r.school_type || 'Coed'} · {r.fee_us || '—'} fee</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {r.ed && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0F392B] text-white font-semibold">ED {r.ed}</span>}
        {r.rea && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37] text-[#0F392B] font-semibold">REA {r.rea}</span>}
        {r.ea && !r.rea && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F9F9F7] text-[#0F392B] border border-[#E2E2E2] font-semibold">EA {r.ea}</span>}
        {r.test_policy && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F9F9F7] text-[#0F392B] border border-[#E2E2E2] font-semibold">Tests: {r.test_policy}</span>}
      </div>
    </Link>
  );
}

function AddToPortalButton({ name, testId = 'school-add-to-portal' }) {
  const { token, isAuthenticated } = useAuth();
  const [state, setState] = useState('idle'); // idle | adding | added | duplicate | error

  const onAdd = async () => {
    if (!isAuthenticated) {
      const params = new URLSearchParams({ next: window.location.pathname, addSchool: name });
      window.location.href = `/sign-up?${params.toString()}`;
      return;
    }
    setState('adding');
    try {
      const res = await fetch(`${API_URL}/api/colleges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, college_type: 'target' }),
      });
      if (res.status === 409) { setState('duplicate'); return; }
      if (!res.ok) throw new Error();
      setState('added');
    } catch {
      setState('error');
    }
  };

  if (state === 'added') {
    return (
      <div className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-[#0F392B] text-sm font-semibold" data-testid={`${testId}-added`}>
        <CheckCircle2 className="h-4 w-4 text-[#0F392B]" /> Added to your portal
        <Link to="/dashboard/colleges" className="ml-2 underline text-[13px] font-medium">Open list</Link>
      </div>
    );
  }
  if (state === 'duplicate') {
    return (
      <div className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-[#0F392B] text-sm font-semibold" data-testid={`${testId}-duplicate`}>
        Already on your list · <Link to="/dashboard/colleges" className="underline text-[13px]">Open</Link>
      </div>
    );
  }
  return (
    <Button
      size="lg"
      onClick={onAdd}
      disabled={state === 'adding'}
      className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl"
      data-testid={testId}
    >
      {state === 'adding' ? (
        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Adding…</>
      ) : (
        <><Plus className="mr-2 h-4 w-4" /> {isAuthenticated ? 'Add to my portal' : 'Add to my portal'}</>
      )}
    </Button>
  );
}

function NotifySection({ slug, name }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/common-app/watch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, slug }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to subscribe');
      setSubscribed(true);
      toast.success(`We'll alert you if ${name} updates its Common App entry.`);
    } catch (err) {
      toast.error(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-14 bg-white border-t border-[#E2E2E2]" data-testid="school-notify">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <Bell className="h-6 w-6 text-[#D4AF37] mx-auto mb-3" strokeWidth={1.75} />
        <h3 className="text-xl md:text-2xl font-medium text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Notify me if {name} changes anything
        </h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-lg mx-auto">
          Deadlines, fees, and test policies do shift mid-cycle. Get one email if this school updates its Common App entry.
        </p>
        {subscribed ? (
          <div className="inline-flex items-center gap-2 text-sm text-[#0F392B] bg-[#F9F9F7] border border-[#E2E2E2] rounded-full px-5 py-3" data-testid="school-notify-success">
            <CheckCircle2 className="h-4 w-4 text-[#0F392B]" /> You&apos;re subscribed. We&apos;ll email you at {email}.
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto" data-testid="school-notify-form">
            <Input
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-11 border-[#E2E2E2] flex-1"
              data-testid="school-notify-input"
            />
            <Button type="submit" disabled={submitting}
              className="bg-[#0F392B] text-white hover:bg-[#0F392B]/90 h-11 px-6 text-sm font-semibold"
              data-testid="school-notify-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Notify me'}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function DeadlinesSchoolPage() {
  const { slug } = useParams();
  const [school, setSchool] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`${API_URL}/api/common-app/requirements/${slug}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); throw new Error('not-found'); }
        return r.json();
      })
      .then((data) => { if (!cancelled) setSchool(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    fetch(`${API_URL}/api/common-app/requirements/${slug}/related?limit=6`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setRelated(d.items || []); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <MarketingNav />
        <div className="pt-32 flex items-center justify-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (notFound || !school) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="School not found in the Common App grid" noindex />
        <MarketingNav />
        <div className="pt-32 pb-24 max-w-2xl mx-auto px-4 text-center">
          <AlertTriangle className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            School not found
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            &ldquo;{slug}&rdquo; isn&apos;t in the Common App 2025-26 grid. Browse all 1,105 colleges instead.
          </p>
          <Link to="/deadlines-grid">
            <Button className="bg-[#0F392B] text-white h-11 px-6 text-sm font-semibold">
              Back to the full grid <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const s = school;
  const canonicalName = s.name;
  const sameAsFee = s.fee_us === s.fee_intl;
  const primaryDeadline = s.ed || s.rea || s.ea || s.rd_rolling;

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollegeOrUniversity",
    "name": canonicalName,
    "url": `https://ivyleagueconsulting.org/deadlines-grid/${slug}`,
    "applicationDeadline": primaryDeadline,
    "sameAs": [`https://www.commonapp.org`],
    "offers": {
      "@type": "Offer",
      "name": "Common Application",
      "price": (s.fee_us || '').replace(/[^0-9.]/g, '') || undefined,
      "priceCurrency": "USD",
    },
  };

  return (
    <div className="min-h-screen bg-white" data-testid="school-detail-page">
      <SEO
        title={`${canonicalName} — Common App Deadlines & Fees 2025-26`}
        description={`${canonicalName} on the Common Application: ${primaryDeadline ? `application deadline ${primaryDeadline}, ` : ''}application fee ${s.fee_us || '—'}${!sameAsFee && s.fee_intl ? ` (${s.fee_intl} international)` : ''}, ${TEST_POLICY_LABELS[s.test_policy] || 'test policy per college'}. Updated Oct 2025.`}
        keywords={`${canonicalName} common app deadline, ${canonicalName} application fee, ${canonicalName} test policy, ${canonicalName} early decision`}
        schema={schema}
      />
      <MarketingNav />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-[#F9F9F7] border-b border-[#E2E2E2]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/deadlines-grid" className="text-xs text-muted-foreground hover:text-[#0F392B] inline-flex items-center gap-1.5" data-testid="breadcrumb-back">
            <ArrowLeft className="h-3 w-3" /> Common App grid
          </Link>
        </div>
      </div>

      {/* Header */}
      <section className="py-12 md:py-16 bg-[#0F392B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Common App 2025-26</p>
          <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="school-detail-name">
            {canonicalName}
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
            <span>{s.school_type || 'Coed'}</span>
            <span>·</span>
            <span>US fee <span className="text-white font-semibold">{s.fee_us || '—'}</span></span>
            {!sameAsFee && s.fee_intl && (<><span>·</span><span>INTL fee <span className="text-white font-semibold">{s.fee_intl}</span></span></>)}
            {s.test_policy && (<><span>·</span><span className="text-[#D4AF37] font-semibold">{TEST_POLICY_LABELS[s.test_policy]}</span></>)}
            {s.state && STATE_NAMES[s.state] && (
              <><span>·</span>
              <Link to={`/deadlines-grid/state/${s.state.toLowerCase()}`} className="underline underline-offset-2 hover:text-white" data-testid="school-state-link">
                All {STATE_NAMES[s.state]} colleges →
              </Link></>
            )}
          </div>
        </div>
      </section>

      {/* Deadlines */}
      <section className="py-12 md:py-14" data-testid="school-deadlines">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-medium text-[#0F392B] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Application deadlines
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {PLAN_LABELS.map((p) => (
              <DeadlineTile key={p.key} short={p.short} label={p.label} value={s[p.key]} testid={`deadline-tile-${p.key}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="pb-12 md:pb-16" data-testid="school-requirements">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E2E2E2] rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0F392B] mb-4 uppercase tracking-widest" style={{ letterSpacing: '0.12em' }}>Common App</h3>
              <div>
                <InfoRow icon={FileText} label="Personal essay" value={s.pe_required === 'Yes' ? 'Required' : (s.pe_required || null)} />
                <InfoRow icon={ClipboardCheck} label="Courses & grades (self-report)" value={s.cg_required === 'SR' ? 'Self-report required' : (s.cg_required === 'Yes' ? 'Required' : (s.cg_required || null))} />
                <InfoRow icon={FileText} label="Portfolio" value={s.portfolio} />
                <InfoRow icon={FileText} label="Writing supplement" value={s.writing} />
              </div>
            </div>
            <div className="bg-white border border-[#E2E2E2] rounded-2xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-[#0F392B] mb-4 uppercase tracking-widest" style={{ letterSpacing: '0.12em' }}>Testing & Recommenders</h3>
              <div>
                <InfoRow icon={ClipboardCheck} label="Test policy" value={TEST_POLICY_LABELS[s.test_policy] || s.test_policy} />
                <InfoRow icon={ClipboardCheck} label="SAT/ACT tests used" value={s.sat_act_used} />
                <InfoRow icon={Globe} label="International English test" value={s.intl_tests} />
                <InfoRow icon={ClipboardCheck} label={`Teacher evals (TE)  ·  Other evals (OE)`} value={[s.te, s.oe].filter(Boolean).join(' · ') || null} />
                <InfoRow icon={Calendar} label="Mid-Year & Counselor Report" value={[s.mr === 'Y' && 'Mid-Year: Yes', s.cr === 'Y' && 'Counselor rec: Yes'].filter(Boolean).join(' · ') || null} />
              </div>
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground text-center italic mt-6">
            Source: Common Application 2025-26 first-year grid, updated Oct 24 2025. Always verify with the college before submitting.
          </p>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-14 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="school-related">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-2">Similar deadlines</p>
              <h2 className="text-2xl font-medium text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Colleges with a similar application profile
              </h2>
              <p className="text-xs text-muted-foreground mt-2 max-w-xl mx-auto">
                Same plan mix and test policy as {canonicalName}. Click any card to see its full grid entry.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {related.map((r) => <RelatedCard key={r.slug} r={r} />)}
            </div>
          </div>
        </section>
      )}

      {/* Notify me */}
      <NotifySection slug={slug} name={canonicalName} />

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="school-cta">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Track {canonicalName} deadlines in your portal.
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Add {canonicalName} to your portal and every deadline, fee, and test policy on this page auto-syncs — no re-entry.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <AddToPortalButton name={canonicalName} testId="school-cta-add-portal" />
            <Link to="/deadlines-grid">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="school-cta-grid">
                Browse all 1,105 <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
